import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { ApiResponse, User, Subscription, QuotaStatus } from '../types'
import {
  classifyCheckoutRedirectUrl,
  getBillingBuildSnapshot,
  logCheckout,
} from '../lib/subscribeCheckoutDebug'

class ApiService {
  private api: AxiosInstance
  /** Absolute `/api/v1` base for OAuth redirects — must match where axios sends API traffic. */
  private readonly resolvedApiV1Base: string

  constructor() {
    const raw = (import.meta as any).env?.VITE_SERVER_URL as string | undefined
    const serverUrl = typeof raw === 'string' ? raw.trim().replace(/\/$/, '') : ''
    const baseURL = serverUrl ? `${serverUrl}/api/v1` : '/api/v1'
    this.resolvedApiV1Base = serverUrl ? `${serverUrl}/api/v1` : `${window.location.origin}/api/v1`

    console.log('API Service - Server URL:', serverUrl || '(empty — same-origin /api/v1)')
    console.log('API Service - Base URL:', baseURL)
    console.log('API Service - OAuth / resolved API base:', this.resolvedApiV1Base)

    this.api = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && !error.config._retry) {
          // Prevent infinite retry loops
          error.config._retry = true
          
          // Token expired, try to refresh
          const refreshToken = localStorage.getItem('refreshToken')
          if (refreshToken) {
            try {
              const response = await this.api.post('/auth/refresh', {
                refreshToken
              })
              const refreshed = response.data?.data || response.data || {}
              const nextToken = refreshed.token || refreshed.accessToken
              if (!nextToken) {
                throw new Error('No token in refresh response')
              }
              localStorage.setItem('authToken', nextToken)
              
              // Retry the original request
              error.config.headers.Authorization = `Bearer ${nextToken}`
              return this.api.request(error.config)
            } catch (refreshError) {
              // Refresh failed, redirect to login
              localStorage.removeItem('authToken')
              localStorage.removeItem('refreshToken')
              window.location.href = '/login'
            }
          } else {
            // No refresh token, redirect to login
            localStorage.removeItem('authToken')
            window.location.href = '/login'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<{ user: User; token: string; refreshToken: string }> {
    // Get machineId from localStorage if available (from Premiere plugin)
    const machineId = localStorage.getItem('auth_machineId')
    
    // Include machineId in request header if available
    const headers: Record<string, string> = {}
    if (machineId) {
      headers['X-Machine-ID'] = machineId
    }
    
    const response: AxiosResponse<ApiResponse<{ user: User; token: string; refreshToken: string }>> =
      await this.api.post('/auth/login', { email, password }, { headers })
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Login failed')
    }
    
    return response.data.data!
  }

  async signup(email: string, password: string): Promise<{ user: User; token: string; refreshToken: string }> {
    const response: AxiosResponse<ApiResponse<{ user: User; token: string; refreshToken: string }>> =
      await this.api.post('/auth/signup', { email, password })
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Signup failed')
    }
    
    return response.data.data!
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout')
  }

  async refreshToken(refreshToken: string): Promise<{ token: string }> {
    const response: AxiosResponse<ApiResponse<{ token: string }>> =
      await this.api.post('/auth/refresh', { refreshToken })
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Token refresh failed')
    }
    
    return response.data.data!
  }

  async forgotPassword(email: string): Promise<void> {
    const response: AxiosResponse<ApiResponse> = 
      await this.api.post('/auth/forgot-password', { email })
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Password reset failed')
    }
  }

  async resetPassword(token: string, password: string): Promise<void> {
    const response: AxiosResponse<ApiResponse> = 
      await this.api.post('/auth/reset-password', { token, password })
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Password reset failed')
    }
  }

  // User endpoints
  async getProfile(): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = 
      await this.api.get('/auth/profile')
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get profile')
    }
    
    return response.data.data!
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = 
      await this.api.put('/auth/profile', data)
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update profile')
    }
    
    return response.data.data!
  }

  // Subscription endpoints
  async getSubscription(): Promise<Subscription | null> {
    const response: AxiosResponse<ApiResponse<Subscription>> = 
      await this.api.get('/subscriptions/current')
    
    if (!response.data.success) {
      return null
    }
    
    return response.data.data || null
  }

  async getPlans(): Promise<{
    plans: any[]
    paypalEnvironment?: 'sandbox' | 'production'
  }> {
    logCheckout('api.getPlans.request', {
      baseURL: this.api.defaults.baseURL,
      build: getBillingBuildSnapshot(),
    })
    const response: AxiosResponse<{
      plans: any[]
      paypalEnvironment?: 'sandbox' | 'production'
    }> = await this.api.get('/subscriptions/plans')

    logCheckout('api.getPlans.response', {
      status: response.status,
      paypalEnvironment: response.data.paypalEnvironment,
      planCount: response.data.plans?.length,
    })

    return response.data
  }

  async createCheckoutSession(
    priceId: string,
    machineId?: string,
    client?: string
  ): Promise<{ url: string }> {
    logCheckout('api.createCheckoutSession.request', {
      baseURL: this.api.defaults.baseURL,
      priceId,
      hasMachineId: !!machineId,
      client: client ?? null,
    })
    const response: AxiosResponse<ApiResponse<{ url: string }>> =
      await this.api.post('/subscriptions/checkout', { priceId, machineId, client })

    if (!response.data.success) {
      const msg =
        (response.data as any).message ||
        response.data.error ||
        'Failed to create checkout session'
      logCheckout('api.createCheckoutSession.errorResponse', {
        status: response.status,
        error: response.data.error,
        message: (response.data as any).message,
        fullData: response.data,
      })
      throw new Error(msg)
    }

    const url = response.data.data!.url
    logCheckout('api.createCheckoutSession.success', {
      classified: classifyCheckoutRedirectUrl(url),
      urlLength: url.length,
      urlPreview: url.length > 200 ? `${url.slice(0, 200)}…` : url,
    })

    return response.data.data!
  }

  async cancelSubscription(): Promise<void> {
    const response: AxiosResponse<ApiResponse> = 
      await this.api.post('/subscriptions/cancel')
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to cancel subscription')
    }
  }

  // Quota endpoints
  async getQuotaStatus(): Promise<QuotaStatus> {
    const response: AxiosResponse<ApiResponse<QuotaStatus>> = 
      await this.api.get('/quota/status')
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get quota status')
    }
    
    return response.data.data!
  }

  // Admin endpoints
  async getUsers(): Promise<User[]> {
    const response: AxiosResponse<ApiResponse<User[]>> = 
      await this.api.get('/admin/users')
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get users')
    }
    
    return response.data.data!
  }

  async getWebappUrl(): Promise<string> {
    const response: AxiosResponse<ApiResponse<{ url: string }>> = 
      await this.api.get('/quota/webapp-url')
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get webapp URL')
    }
    
    return response.data.data!.url
  }

  // Google OAuth
  getGoogleAuthUrl(): string {
    const currentUrl = window.location.origin + window.location.pathname
    const returnUrl = encodeURIComponent(currentUrl)
    
    // Get client, machineId, redirect, and eventType from localStorage if available (from Premiere plugin)
    const client = localStorage.getItem('auth_client')
    const machineId = localStorage.getItem('auth_machineId')
    const redirect = localStorage.getItem('auth_redirect') // Original path before login redirect
    const eventType = localStorage.getItem('auth_eventType') || 'signin' // 'signin' or 'upgrade'
    
    let url = `${this.resolvedApiV1Base}/auth/google?return=${returnUrl}`
    
    if (client) {
      url += `&client=${encodeURIComponent(client)}`
    }
    
    if (machineId) {
      url += `&machineId=${encodeURIComponent(machineId)}`
    }
    
    // Pass redirect path and eventType in OAuth state so callback can use them
    if (redirect) {
      url += `&redirect=${encodeURIComponent(redirect)}`
    }
    url += `&eventType=${encodeURIComponent(eventType)}`
    
    return url
  }
}

export const apiService = new ApiService()
export default apiService
